import { useEffect, useRef } from 'react';
import {
  NEW_TRIAL_EXPIRED_A,
  NEW_TRIAL_EXPIRED_B,
  OLD_TRIAL_EXPIRED_A,
  OLD_TRIAL_EXPIRED_B,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useStorage } from 'commons/hooks';
import { logError } from 'commons/logger';
import CanceledStorePage from 'domains/Account/pages/CanceledStorePage';
import {
  useAdminDisabledReason,
  useGetStoreInfo,
  useHasFirstPayment,
} from 'domains/Auth/hooks';
import useAdminDisabled from 'domains/Auth/hooks/useAdminDisabled';
import useRefreshInfoStoreUser from 'domains/Auth/hooks/useRefreshInfoToSync';
import UpsellBlockPage from 'domains/Billing/UpsellFlow/pages/UpsellBlockPage';
import { AppLoading } from 'domains/PartnersApps/components/EmbeddedApp';
import { useSetTrialExpiredVariantMutation } from './trialExpiredAbTestApi';
import { useUpsellBlock } from '../UpsellFlow/hooks';

const CANCELLED_STATE_DECIDED = 'cancelled-state-decided';

function CancelledStateDecider() {
  const { id } = useGetStoreInfo();
  const isAdminDisabled = useAdminDisabled();
  const adminDisabledReason = useAdminDisabledReason();
  const hasFirstPayment = useHasFirstPayment();
  const [
    hasOldTrialExpiredATag,
    hasOldTrialExpiredBTag,
    hasNewTrialExpiredATag,
    hasNewTrialExpiredBTag,
  ] = useHasTags([
    OLD_TRIAL_EXPIRED_A,
    OLD_TRIAL_EXPIRED_B,
    NEW_TRIAL_EXPIRED_A,
    NEW_TRIAL_EXPIRED_B,
  ]);
  const { upsellHardBlock } = useUpsellBlock();
  const [
    setTrialExpiredVariantMutation,
    { isLoading: isSetTrialExpiredVariantLoading },
  ] = useSetTrialExpiredVariantMutation();
  const { refreshInfo } = useRefreshInfoStoreUser();
  const mutationInProgress = useRef(false);
  const localStorageKey = `${CANCELLED_STATE_DECIDED}-${id}`;
  const [
    cancelledStateCompleted,
    setCancelledStateCompleted,
    cancelledStateCompletedLoaded,
  ] = useStorage(localStorageKey, false);

  const hasAnyTrialExpiredTag =
    hasOldTrialExpiredATag ||
    hasOldTrialExpiredBTag ||
    hasNewTrialExpiredATag ||
    hasNewTrialExpiredBTag;

  useEffect(() => {
    if (
      cancelledStateCompletedLoaded &&
      !cancelledStateCompleted &&
      !upsellHardBlock &&
      isAdminDisabled &&
      adminDisabledReason === 'expired' &&
      !hasFirstPayment &&
      !hasAnyTrialExpiredTag &&
      !mutationInProgress.current
    ) {
      mutationInProgress.current = true;
      setTrialExpiredVariantMutation()
        .unwrap()
        .then(() => {
          setCancelledStateCompleted(true);
          refreshInfo();
        })
        .catch((error) => {
          logError('CancelledStateDecider:setTrialExpiredVariant', error);
        });
    }
  }, [
    adminDisabledReason,
    cancelledStateCompleted,
    cancelledStateCompletedLoaded,
    hasAnyTrialExpiredTag,
    hasFirstPayment,
    isAdminDisabled,
    refreshInfo,
    setCancelledStateCompleted,
    setTrialExpiredVariantMutation,
    upsellHardBlock,
  ]);

  if (isSetTrialExpiredVariantLoading) {
    return <AppLoading />;
  }

  return upsellHardBlock ? <UpsellBlockPage /> : <CanceledStorePage />;
}

export default CancelledStateDecider;
